import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ListTodo, RotateCcw, Search, Check, SkipForward,
  ChevronLeft, ChevronRight, AlertCircle, ExternalLink, Flag,
} from "lucide-react";

import { API } from "@/lib/api";
import { readToken } from "@/lib/tokenStore";
import EmptyState from "@/components/journey/EmptyState";

const VIEW_OPTIONS = [
  { value: "open_today", label: "Heute fällig", status: "open", due: "today" },
  { value: "open_overdue", label: "Überfällig", status: "open", due: "overdue" },
  { value: "open_week", label: "Diese Woche", status: "open", due: "week" },
  { value: "open_all", label: "Alle offenen", status: "open", due: "all" },
  { value: "done", label: "Erledigt", status: "done", due: "all" },
  { value: "all", label: "Alle", status: "all", due: "all" },
];

const PRIORITIES = [
  { value: "", label: "Alle Prioritäten" },
  { value: "urgent", label: "Dringend" },
  { value: "high", label: "Hoch" },
  { value: "normal", label: "Normal" },
  { value: "low", label: "Niedrig" },
];

const TYPES = [
  { value: "", label: "Alle Typen" },
  { value: "follow_up", label: "Follow-up" },
  { value: "call", label: "Anrufen" },
  { value: "email", label: "Mail senden" },
  { value: "meeting_prep", label: "Termin vorbereiten" },
  { value: "request_documents", label: "Unterlagen anfordern" },
  { value: "review_documents", label: "Unterlagen prüfen" },
  { value: "prepare_offer", label: "Angebot vorbereiten" },
  { value: "internal_review", label: "Interne Prüfung" },
  { value: "partner_review", label: "Partner prüfen" },
  { value: "dev_review", label: "Dev-Einreichung prüfen" },
  { value: "other", label: "Sonstiges" },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map((o) => [o.value, o.label]));

const PRIO_TONE = {
  low: "text-zinc-400", normal: "text-zinc-300",
  high: "text-amber-300", urgent: "text-red-300",
};

const PAGE_SIZE = 25;

function headers() {
  const t = readToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function isOverdue(iso) {
  if (!iso) return false;
  try { return new Date(iso).getTime() < Date.now(); } catch { return false; }
}

function useDebounced(value, delay) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function GlobalTasks() {
  const [view, setView] = useState("open_today");
  const [priority, setPriority] = useState("");
  const [type, setType] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounced(searchInput, 350);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const config = useMemo(() => VIEW_OPTIONS.find((v) => v.value === view), [view]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("status", config.status);
      params.set("due", config.due);
      if (priority) params.set("priority", priority);
      if (type) params.set("type", type);
      if (search) params.set("search", search);
      params.set("page", String(page));
      params.set("page_size", String(PAGE_SIZE));
      const r = await axios.get(`${API}/admin/crm/tasks?${params.toString()}`, {
        headers: headers(), timeout: 20000,
      });
      setData(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Fehler");
      setData(null);
    } finally { setLoading(false); }
  }, [config, priority, type, search, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [view, priority, type, search]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/crm/tasks/${id}`, { status }, { headers: headers() });
      toast.success(status === "done" ? "Erledigt" : "Übersprungen");
      await load();
    } catch {
      toast.error("Aktion fehlgeschlagen");
    }
  };

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = data?.total_pages || 0;

  return (
    <div data-testid="journey-global-tasks" className="space-y-6">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-zinc-100">
          <ListTodo className="h-5 w-5 text-emerald-400" />
          Aufgaben & Follow-ups
        </h1>
        <p className="text-sm text-zinc-400">
          Alle offenen nächsten Schritte aus PINE Journey Control.
        </p>
      </header>

      <nav data-testid="tasks-view-tabs" className="flex flex-wrap gap-1 border-b border-white/[0.06] pb-2">
        {VIEW_OPTIONS.map((v) => (
          <button
            key={v.value}
            type="button"
            onClick={() => setView(v.value)}
            data-testid={`tasks-view-${v.value}`}
            className={[
              "rounded px-3 py-1.5 text-xs transition",
              view === v.value
                ? "bg-emerald-500/10 text-emerald-300"
                : "text-zinc-400 hover:bg-white/[0.04] hover:text-zinc-100",
            ].join(" ")}
          >
            {v.label}
          </button>
        ))}
      </nav>

      <section className="space-y-3 rounded-md border border-white/[0.06] bg-white/[0.01] p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <input
              type="text" value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Suche nach Titel oder Beschreibung…"
              data-testid="tasks-search"
              className="w-full rounded border border-white/10 bg-[#0B0F10] py-2 pl-9 pr-3 text-sm text-zinc-100 outline-none transition focus:border-emerald-500/40"
            />
          </div>
          <button type="button" onClick={load} className="inline-flex items-center justify-center gap-1.5 rounded border border-white/10 px-3 py-2 text-xs text-zinc-300 hover:bg-white/5">
            <RotateCcw className="h-3.5 w-3.5" />
            Aktualisieren
          </button>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="tasks-filter-priority" className="flex-1 rounded border border-white/10 bg-[#0B0F10] px-2 py-2 text-xs text-zinc-200">
            {PRIORITIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={type} onChange={(e) => setType(e.target.value)} data-testid="tasks-filter-type" className="flex-1 rounded border border-white/10 bg-[#0B0F10] px-2 py-2 text-xs text-zinc-200">
            {TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </section>

      {error ? (
        <div className="rounded-md border border-red-500/30 bg-red-500/[0.05] p-4 text-sm text-red-200">
          <AlertCircle className="mr-1.5 inline h-3.5 w-3.5" />{String(error)}
        </div>
      ) : loading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-14 animate-pulse rounded bg-white/[0.03]" />)}
        </div>
      ) : items.length === 0 ? (
        <EmptyState testId="tasks-empty" icon={ListTodo} title="Keine Aufgaben" description="Keine Aufgaben für diese Ansicht." />
      ) : (
        <>
          <div data-testid="tasks-table-wrap" className="hidden overflow-hidden rounded-md border border-white/[0.06] lg:block">
            <table className="w-full table-fixed">
              <colgroup>
                <col className="w-[150px]" />
                <col className="w-[260px]" />
                <col className="w-[180px]" />
                <col className="w-[120px]" />
                <col className="w-[100px]" />
                <col className="w-[100px]" />
              </colgroup>
              <thead className="bg-white/[0.02] text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Fällig</th>
                  <th className="px-4 py-3 font-medium">Aufgabe</th>
                  <th className="px-4 py-3 font-medium">Lead</th>
                  <th className="px-4 py-3 font-medium">Typ</th>
                  <th className="px-4 py-3 font-medium">Priorität</th>
                  <th className="px-4 py-3 text-right font-medium">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => {
                  const overdue = t.status === "open" && isOverdue(t.due_at);
                  return (
                    <tr key={t.id} data-testid={`global-task-${t.id}`} className={`border-b border-white/[0.04] text-sm ${overdue ? "bg-red-500/[0.03]" : ""}`}>
                      <td className={`px-4 py-3 text-xs ${overdue ? "text-red-300" : "text-zinc-400"}`}>{fmt(t.due_at)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-zinc-100">{t.title}</div>
                        {t.description ? <div className="text-xs text-zinc-500 truncate">{t.description}</div> : null}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <Link to={`/admin/journey/leads/${t.lead?.lead_id || t.lead_id}`} className="inline-flex items-center gap-1 text-emerald-300 hover:underline">
                          {t.lead?.full_name || "—"}
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                        {t.lead?.company ? <div className="text-[10px] text-zinc-500">{t.lead.company}</div> : null}
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-300">{TYPE_LABEL[t.type] || t.type}</td>
                      <td className={`px-4 py-3 text-xs ${PRIO_TONE[t.priority] || ""}`}>
                        <Flag className="mr-0.5 inline h-3 w-3" />
                        {t.priority}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {t.status === "open" ? (
                          <div className="inline-flex gap-1">
                            <button type="button" onClick={() => setStatus(t.id, "done")} data-testid={`global-task-done-${t.id}`} className="rounded p-1 text-zinc-400 hover:bg-emerald-500/10 hover:text-emerald-300" title="Erledigen">
                              <Check className="h-3.5 w-3.5" />
                            </button>
                            <button type="button" onClick={() => setStatus(t.id, "skipped")} data-testid={`global-task-skip-${t.id}`} className="rounded p-1 text-zinc-400 hover:bg-amber-500/10 hover:text-amber-300" title="Überspringen">
                              <SkipForward className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-[10px] text-zinc-500">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div data-testid="tasks-cards-wrap" className="grid gap-3 lg:hidden">
            {items.map((t) => {
              const overdue = t.status === "open" && isOverdue(t.due_at);
              return (
                <Link key={t.id} to={`/admin/journey/leads/${t.lead?.lead_id || t.lead_id}`}
                  className={`block space-y-2 rounded-md border p-4 ${overdue ? "border-red-500/30 bg-red-500/[0.04]" : "border-white/[0.06] bg-white/[0.01]"}`}>
                  <div className={`text-[10px] uppercase tracking-[0.18em] ${overdue ? "text-red-300" : "text-zinc-500"}`}>
                    Fällig {fmt(t.due_at)}
                  </div>
                  <div className="text-sm font-medium text-zinc-100">{t.title}</div>
                  <div className="text-xs text-zinc-400">{t.lead?.full_name || "—"} · {t.lead?.company || "—"}</div>
                  <div className="flex gap-2 text-[10px] text-zinc-500">
                    <span>{TYPE_LABEL[t.type]}</span>
                    <span>·</span>
                    <span className={PRIO_TONE[t.priority]}>{t.priority}</span>
                  </div>
                </Link>
              );
            })}
          </div>

          {totalPages > 1 ? (
            <div className="flex items-center justify-between text-xs text-zinc-400">
              <div>{total} Aufgaben — Seite {page} von {totalPages}</div>
              <div className="flex items-center gap-2">
                <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} className="inline-flex items-center gap-1 rounded border border-white/10 px-2 py-1 disabled:opacity-40">
                  <ChevronLeft className="h-3.5 w-3.5" />Zurück
                </button>
                <button type="button" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="inline-flex items-center gap-1 rounded border border-white/10 px-2 py-1 disabled:opacity-40">
                  Weiter<ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ) : (
            <div className="text-right text-xs text-zinc-500">{total} Ergebnis{total === 1 ? "" : "se"}</div>
          )}
        </>
      )}
    </div>
  );
}
